import { api } from '@/lib/api';
import {
  BoampSearchParams,
  ExternalSearchResult,
  ImportItem,
  ImportResult,
  ExcludedOpportunity,
  CheckResult,
  PaginatedResponse,
} from '../types/opportunity.types';

const BASE_URL = '/tm/import';

/**
 * Service pour la recherche et l'import d'opportunités BOAMP
 */
export const boampSearchService = {
  /**
   * Recherche des appels d'offres sur BOAMP
   */
  async search(params: BoampSearchParams): Promise<ExternalSearchResult> {
    const searchParams = new URLSearchParams();

    // Nouveaux filtres principaux
    if (params.idweb) searchParams.set('idweb', params.idweb);
    if (params.descriptors?.length) {
      searchParams.set('descriptors', params.descriptors.join(','));
    }
    if (params.buyerName) searchParams.set('buyerName', params.buyerName);
    if (params.nature) searchParams.set('nature', params.nature);

    // Types de marché
    if (params.marketTypes?.length) {
      searchParams.set('marketTypes', params.marketTypes.join(','));
    }

    // Localisation
    if (params.departments?.length) {
      searchParams.set('departments', params.departments.join(','));
    }

    // Dates de publication
    if (params.publicationAfter) searchParams.set('publicationAfter', params.publicationAfter);
    if (params.publicationBefore) searchParams.set('publicationBefore', params.publicationBefore);

    // Dates limites
    if (params.deadlineAfter) searchParams.set('deadlineAfter', params.deadlineAfter);
    if (params.deadlineBefore) searchParams.set('deadlineBefore', params.deadlineBefore);

    // Montants
    if (params.amountMin !== undefined) searchParams.set('amountMin', String(params.amountMin));
    if (params.amountMax !== undefined) searchParams.set('amountMax', String(params.amountMax));

    // Pagination et tri
    if (params.page) searchParams.set('page', String(params.page));
    if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));
    if (params.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);

    const response = await api.get<ExternalSearchResult>(
      `${BASE_URL}/search?${searchParams.toString()}`
    );
    console.log("requete : ",searchParams.toString())
    return response.data;
  },

  /**
   * Importe les opportunités sélectionnées
   */
  async importOpportunities(items: ImportItem[]): Promise<ImportResult> {
    const response = await api.post<ImportResult>(BASE_URL, { items });
    return response.data;
  },

  /**
   * Vérifie si des opportunités sont déjà importées ou exclues
   */
  async checkOpportunities(
    externalIds: string[],
    source: 'boamp' | 'ted'
  ): Promise<CheckResult> {
    const response = await api.post<CheckResult>(`${BASE_URL}/check`, {
      externalIds,
      source,
    });
    return response.data;
  },

  /**
   * Exclut une opportunité des futures recherches
   */
  async excludeOpportunity(
    externalId: string,
    source: 'boamp' | 'ted',
    reason?: string
  ): Promise<ExcludedOpportunity> {
    const response = await api.post<ExcludedOpportunity>(`${BASE_URL}/exclude`, {
      externalId,
      source,
      reason,
    });
    return response.data;
  },

  /**
   * Réintègre une opportunité exclue
   */
  async removeExclusion(externalId: string, source: 'boamp' | 'ted'): Promise<void> {
    await api.delete(`${BASE_URL}/exclude/${source}/${externalId}`);
  },

  /**
   * Liste les opportunités exclues
   */
  async getExcludedOpportunities(
    page = 1,
    pageSize = 20
  ): Promise<PaginatedResponse<ExcludedOpportunity>> {
    const response = await api.get<{
      data: ExcludedOpportunity[];
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }>(`${BASE_URL}/excluded?page=${page}&pageSize=${pageSize}`);

    return {
      data: response.data.data,
      meta: {
        total: response.data.total,
        page: response.data.page,
        pageSize: response.data.pageSize,
        totalPages: response.data.totalPages,
      },
    };
  },
};
